import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import {
  Printer, Plus, Pencil, Trash2, Save, X, Copy, RefreshCw,
  Wifi, WifiOff, Zap, Loader2, LayoutGrid, MapPin, Radar, Download, Eye,
} from "lucide-react";
import TicketPreviewModal from "../../components/company/TicketPreviewModal";

const BRIDGE_DOWNLOAD_URL =
  `${(process.env.REACT_APP_API_URL || "").replace(/\/api\/?$/, "")}/bridge/qkiere-bridge-windows.zip`;

const inp = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 bg-white";
const btn = "px-4 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center gap-2";

const STATION_EMPTY = { name: "", connectionType: "network", printerIp: "", printerPort: 9100, printerName: "", paperWidth: 48 };

function getCompanyIdFromStorage() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    return u?.companyId ?? u?.company?.id ?? null;
  } catch { return null; }
}

export default function CompanyPrinters() {
  const companyId = getCompanyIdFromStorage();

  const [sedes, setSedes] = useState([]);
  const [sedeId, setSedeId] = useState("");
  const [loadingSedes, setLoadingSedes] = useState(true);

  const [stations, setStations] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [mappings, setMappings] = useState([]); // [{seccionId, stationId}]
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showToken, setShowToken] = useState(false);
  const [discovered, setDiscovered] = useState([]);
  const [discoveredWindows, setDiscoveredWindows] = useState([]);
  const [scanning, setScanning] = useState(false);

  const [modal, setModal] = useState(null); // null | 'create' | station
  const [form, setForm] = useState(STATION_EMPTY);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [showTicketPreview, setShowTicketPreview] = useState(false);

  // Las impresoras son por sede — primero hay que saber qué sedes tiene la empresa.
  // Un empleado solo administra las impresoras de su propia sede; el dueño/admin ve todas.
  useEffect(() => {
    if (!companyId) return;
    Promise.all([
      API.get(`/sedes/company/${companyId}`),
      API.get("/auth/me").catch(() => null),
    ])
      .then(([res, meRes]) => {
        let list = res.data?.data?.sedes ?? res.data?.data ?? [];
        const user = meRes?.data?.data?.user;
        const isOwner = !!(user?.company?.ownerId && user.id === user.company.ownerId);
        if (user && !isOwner && user.role !== "admin") {
          list = list.filter((s) => s.id === user.sedeId);
        }
        setSedes(list);
        if (list.length > 0) setSedeId(list[0].id);
      })
      .catch(() => {})
      .finally(() => setLoadingSedes(false));
  }, [companyId]);

  const loadAll = useCallback(async () => {
    if (!sedeId) return;
    setLoading(true);
    try {
      const [s1, s2, s3, s4, s5, s6] = await Promise.allSettled([
        API.get("/print-stations", { params: { sedeId } }),
        API.get(`/secciones/company/${companyId}`),
        API.get("/print-stations/bridge-token", { params: { sedeId } }),
        API.get("/print-stations/seccion-mapping", { params: { sedeId } }),
        API.get("/print-stations/discovered", { params: { sedeId } }),
        API.get("/print-stations/discovered-windows", { params: { sedeId } }),
      ]);
      if (s1.status === "fulfilled") setStations(s1.value.data?.data?.stations ?? []);
      if (s2.status === "fulfilled") setSecciones(s2.value.data?.data?.secciones ?? []);
      if (s3.status === "fulfilled") setToken(s3.value.data?.data?.token ?? null);
      if (s4.status === "fulfilled") setMappings(s4.value.data?.data?.mappings ?? []);
      if (s5.status === "fulfilled") setDiscovered(s5.value.data?.data?.printers ?? []);
      if (s6.status === "fulfilled") setDiscoveredWindows(s6.value.data?.data?.printers ?? []);
    } catch {
      toast.error("Error cargando impresoras");
    } finally {
      setLoading(false);
    }
  }, [companyId, sedeId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openCreate = () => { setForm(STATION_EMPTY); setModal("create"); };
  const openEdit = (s) => { setForm({ ...s }); setModal(s); };
  const closeModal = () => { setModal(null); setForm(STATION_EMPTY); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.warn("El nombre es requerido"); return; }
    if (form.connectionType === "windows") {
      if (!form.printerName?.trim()) { toast.warn("El nombre de la impresora de Windows es requerido"); return; }
    } else if (!form.printerIp.trim()) {
      toast.warn("La IP de la impresora es requerida"); return;
    }
    setSaving(true);
    try {
      if (modal === "create") {
        await API.post("/print-stations", { ...form, sedeId });
        toast.success("Estación creada");
      } else {
        await API.patch(`/print-stations/${modal.id}`, form);
        toast.success("Estación actualizada");
      }
      await loadAll();
      closeModal();
    } catch (err) { toast.error(err.response?.data?.message || "Error"); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (s) => {
    try { await API.patch(`/print-stations/${s.id}`, { active: !s.active }); await loadAll(); }
    catch { toast.error("Error al cambiar estado"); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`¿Eliminar la estación "${s.name}"? Las secciones que la usaban quedarán sin estación.`)) return;
    try { await API.delete(`/print-stations/${s.id}`); await loadAll(); toast.success("Eliminada"); }
    catch { toast.error("Error al eliminar"); }
  };

  const handleTestPrint = async (s) => {
    setTestingId(s.id);
    try {
      await API.post(`/print-stations/${s.id}/test-print`);
      toast.success(`Ticket de prueba enviado al puente para "${s.name}"`);
    } catch (err) { toast.error(err.response?.data?.message || "Error enviando la prueba"); }
    finally { setTestingId(null); }
  };

  const handleAssignStation = async (seccion, stationId) => {
    try {
      await API.put("/print-stations/seccion-mapping", { sedeId, seccionId: seccion.id, stationId: stationId || null });
      setMappings((prev) => {
        const next = prev.filter((m) => m.seccionId !== seccion.id);
        if (stationId) next.push({ seccionId: seccion.id, stationId });
        return next;
      });
    } catch (err) { toast.error(err.response?.data?.message || "Error al asignar"); }
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm("Esto invalidará el puente que esté corriendo con el token actual hasta que lo reconfigures. ¿Continuar?")) return;
    try {
      const res = await API.post("/print-stations/bridge-token/regenerate", { sedeId });
      setToken(res.data?.data?.token);
      setShowToken(true);
      toast.success("Token regenerado");
    } catch { toast.error("Error regenerando el token"); }
  };

  const copyToken = () => {
    if (!token) return;
    navigator.clipboard.writeText(token).then(() => toast.success("Token copiado"));
  };

  // El escaneo lo hace el bridge (ve la red local y el spooler de Windows del equipo
  // donde corre, el navegador no ve ninguno de los dos) — el backend solo reenvía el
  // pedido por socket. El resultado llega async, así que reconsultamos "discovered" y
  // "discovered-windows" unas veces en lo que el bridge termina y reporta.
  const handleScan = async () => {
    setScanning(true);
    try {
      await API.post("/print-stations/scan", { sedeId });
      toast.info("Buscando impresoras (red y Windows) desde el puente…");
      for (let i = 0; i < 6; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const [res, resWin] = await Promise.all([
          API.get("/print-stations/discovered", { params: { sedeId } }),
          API.get("/print-stations/discovered-windows", { params: { sedeId } }),
        ]);
        setDiscovered(res.data?.data?.printers ?? []);
        setDiscoveredWindows(resWin.data?.data?.printers ?? []);
      }
    } catch (err) { toast.error(err.response?.data?.message || "Error solicitando el escaneo"); }
    finally { setScanning(false); }
  };

  const openCreateFromDiscovered = (ip) => {
    setForm({ ...STATION_EMPTY, connectionType: "network", printerIp: ip });
    setModal("create");
  };

  const openCreateFromDiscoveredWindows = (name) => {
    setForm({ ...STATION_EMPTY, connectionType: "windows", printerName: name });
    setModal("create");
  };

  const alreadyAdded = (ip) => stations.some((s) => s.connectionType !== "windows" && s.printerIp === ip);
  const alreadyAddedWindows = (name) => stations.some((s) => s.connectionType === "windows" && s.printerName === name);

  if (loadingSedes) {
    return (
      <div className="flex justify-center mt-20">
        <Loader2 size={32} className="animate-spin text-supernova-500" />
      </div>
    );
  }

  // Las impresoras están ligadas a la red local de una sede física — no hay forma de
  // enrutar un trabajo de impresión sin saber a cuál sede pertenece. A diferencia del
  // resto del panel (que degrada con gracia sin sedes), aquí es un bloqueo real.
  if (sedes.length === 0) {
    return (
      <div className="max-w-xl mx-auto mt-20 text-center">
        <Printer size={40} className="mx-auto mb-3 text-gray-300" />
        <h3 className="font-bold text-gray-800 mb-2">Necesitas crear una sede primero</h3>
        <p className="text-sm text-gray-500 mb-4">
          Las impresoras están ligadas a la red local de una sede física — no hay forma de
          enrutar un trabajo de impresión sin saber a cuál sede pertenece.
        </p>
        <Link to="/company/sedes" className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 inline-flex`}>
          <MapPin size={14} /> Crear sede
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Printer className="text-supernova-600" size={26} /> Impresoras
          </h2>
          <p className="text-sm text-gray-500 mt-1">Estaciones de impresión (cocina, barra, etc.) y el puente que las conecta</p>
        </div>
        <button
          type="button"
          onClick={() => setShowTicketPreview(true)}
          className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50 self-start sm:self-auto`}
        >
          <Eye size={14} /> Vista previa del ticket
        </button>
        {sedes.length > 1 ? (
          <select
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white self-start sm:self-auto"
            value={sedeId}
            onChange={(e) => setSedeId(e.target.value)}
          >
            {sedes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        ) : (
          <span className="flex items-center gap-1.5 text-sm text-gray-500 self-start sm:self-auto">
            <MapPin size={14} /> {sedes[0].name}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 size={28} className="animate-spin text-supernova-500" />
        </div>
      ) : (
        <>
          {/* Bridge token */}
          <div className="bg-white rounded-2xl shadow p-5">
            <h3 className="text-base font-bold text-gray-700 mb-2">Token del puente de impresión — {sedes.find((s) => s.id === sedeId)?.name}</h3>
            <p className="text-sm text-gray-500 mb-3">
              Este token conecta el puente local de ESTA sede (un proceso que corre en un equipo de su red, no en internet).
              Cada sede necesita su propio puente y su propio token. Pégalo en el archivo <code className="bg-gray-100 px-1 rounded">.env</code> del puente.
            </p>
            {token ? (
              <div className="flex items-center gap-2 mb-3">
                <code className="flex-1 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5 text-xs font-mono truncate">
                  {showToken ? token : "•".repeat(40)}
                </code>
                <button onClick={() => setShowToken((v) => !v)} className={`${btn} border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs`}>
                  {showToken ? "Ocultar" : "Mostrar"}
                </button>
                <button onClick={copyToken} className={`${btn} border border-gray-200 text-gray-600 hover:bg-gray-50 text-xs`}>
                  <Copy size={13} /> Copiar
                </button>
              </div>
            ) : (
              <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-3">
                Todavía no has generado un token para esta sede.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <button onClick={handleRegenerateToken} className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50`}>
                <RefreshCw size={14} /> {token ? "Regenerar token" : "Generar token"}
              </button>
              <a href={BRIDGE_DOWNLOAD_URL} className={`${btn} bg-supernova-500 hover:bg-supernova-600 text-white`}>
                <Download size={14} /> Descargar puente (Windows)
              </a>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Descomprime el .zip en el equipo de la sede que va a quedar siempre encendido, ejecuta <code className="bg-gray-100 px-1 rounded">install.bat</code> y pega ahí el token de arriba cuando te lo pida.
            </p>
          </div>

          {/* Impresoras detectadas en la red por el bridge */}
          <div className="bg-white rounded-2xl shadow p-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-base font-bold text-gray-700 flex items-center gap-2">
                <Radar size={16} className="text-supernova-500" /> Impresoras en la red
              </h3>
              <button onClick={handleScan} disabled={scanning} className={`${btn} border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-60`}>
                {scanning ? <Loader2 size={14} className="animate-spin" /> : <Radar size={14} />} {scanning ? "Buscando…" : "Buscar impresoras"}
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              El puente de esta sede escanea su red local y reporta qué impresoras encontró — así no tienes
              que sacarle la IP a la impresora manualmente. Necesita que el puente esté corriendo y conectado.
            </p>
            {discovered.length === 0 ? (
              <p className="text-sm text-gray-400 py-2">
                {scanning ? "Esperando resultados del bridge…" : 'Sin resultados todavía — usa "Buscar impresoras".'}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {discovered.map((p) => (
                  <div key={`${p.ip}:${p.port}`} className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2">
                    <span className="text-xs font-mono text-gray-600">{p.ip}:{p.port}</span>
                    {alreadyAdded(p.ip) ? (
                      <span className="text-xs text-green-600 font-semibold">Ya agregada</span>
                    ) : (
                      <button onClick={() => openCreateFromDiscovered(p.ip)} className={`${btn} text-xs bg-supernova-50 text-supernova-700 hover:bg-supernova-100 py-1 px-2`}>
                        <Plus size={11} /> Usar
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Impresoras instaladas en Windows (USB u otra) detectadas por el bridge */}
          <div className="bg-white rounded-2xl shadow p-5">
            <h3 className="text-base font-bold text-gray-700 flex items-center gap-2 mb-2">
              <Printer size={16} className="text-supernova-500" /> Impresoras de Windows (USB u otra)
            </h3>
            <p className="text-sm text-gray-500 mb-3">
              El puente también lista las impresoras instaladas en el computador donde corre — útil para impresoras
              conectadas por USB que no tienen IP propia. Usa el mismo botón "Buscar impresoras" de arriba.
            </p>
            {discoveredWindows.length === 0 ? (
              <p className="text-sm text-gray-400 py-2">
                {scanning ? "Esperando resultados del bridge…" : 'Sin resultados todavía — usa "Buscar impresoras".'}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {discoveredWindows.map((p) => (
                  <div key={p.name} className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2">
                    <span className="text-xs text-gray-600">{p.name}</span>
                    {alreadyAddedWindows(p.name) ? (
                      <span className="text-xs text-green-600 font-semibold">Ya agregada</span>
                    ) : (
                      <button onClick={() => openCreateFromDiscoveredWindows(p.name)} className={`${btn} text-xs bg-supernova-50 text-supernova-700 hover:bg-supernova-100 py-1 px-2`}>
                        <Plus size={11} /> Usar
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Stations CRUD */}
          <div className="bg-white rounded-2xl shadow p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-700">Estaciones</h3>
              <button onClick={openCreate} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600`}>
                <Plus size={14} /> Nueva estación
              </button>
            </div>

            {stations.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <Printer size={36} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">Sin estaciones configuradas en esta sede</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {stations.map((s) => (
                  <div key={s.id} className={`border rounded-2xl p-4 shadow-sm ${s.active ? "border-gray-200" : "border-gray-100 opacity-60"}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="font-bold text-gray-800 text-sm truncate">{s.name}</p>
                      <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-semibold ${s.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                        {s.active ? "Activa" : "Inactiva"}
                      </span>
                    </div>
                    <p className="text-xs font-mono text-gray-500 bg-gray-50 rounded-lg px-2.5 py-1.5 mb-3">
                      {s.connectionType === "windows" ? `Windows: ${s.printerName}` : `${s.printerIp}:${s.printerPort}`} · {s.paperWidth} col
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => handleTestPrint(s)} disabled={testingId === s.id} className={`${btn} text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50`}>
                        {testingId === s.id ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />} Probar
                      </button>
                      <button onClick={() => handleToggleActive(s)} className={`${btn} text-xs border ${s.active ? "border-gray-200 text-gray-600 hover:bg-gray-50" : "border-green-200 text-green-700 hover:bg-green-50"}`}>
                        {s.active ? <WifiOff size={11} /> : <Wifi size={11} />} {s.active ? "Desactivar" : "Activar"}
                      </button>
                      <button onClick={() => openEdit(s)} className={`${btn} text-xs bg-gray-50 text-gray-600 hover:bg-gray-100`}>
                        <Pencil size={11} />
                      </button>
                      <button onClick={() => handleDelete(s)} className={`${btn} text-xs bg-red-50 text-red-500 hover:bg-red-100 ml-auto`}>
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Seccion -> station mapping */}
          <div className="bg-white rounded-2xl shadow p-5">
            <h3 className="text-base font-bold text-gray-700 mb-1 flex items-center gap-2">
              <LayoutGrid size={16} className="text-supernova-500" /> Secciones del menú en esta sede
            </h3>
            <p className="text-sm text-gray-500 mb-4">Cada sección imprime en la estación que le asignes AQUÍ — la misma sección puede ir a una estación distinta en cada sede.</p>
            {secciones.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">Sin secciones todavía</p>
            ) : (
              <div className="space-y-2">
                {secciones.map((sec) => {
                  const current = mappings.find((m) => m.seccionId === sec.id)?.stationId ?? "";
                  return (
                    <div key={sec.id} className="flex items-center justify-between gap-3 border-b border-gray-50 pb-2">
                      <span className="text-sm text-gray-700">{sec.name}</span>
                      <select
                        className={`${inp} max-w-[200px]`}
                        value={current}
                        onChange={(e) => handleAssignStation(sec, e.target.value)}
                      >
                        <option value="">Sin estación</option>
                        {stations.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Station modal */}
      {modal !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">{modal === "create" ? "Nueva estación" : "Editar estación"}</h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Nombre *</label>
                <input className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Cocina, Barra..." />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Tipo de conexión</label>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setForm({ ...form, connectionType: "network" })}
                    className={`${btn} flex-1 justify-center text-xs ${form.connectionType === "network" ? "bg-supernova-500 text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                    Red (IP)
                  </button>
                  <button type="button" onClick={() => setForm({ ...form, connectionType: "windows" })}
                    className={`${btn} flex-1 justify-center text-xs ${form.connectionType === "windows" ? "bg-supernova-500 text-white" : "border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                    Windows (USB u otra)
                  </button>
                </div>
              </div>
              {form.connectionType === "windows" ? (
                <div>
                  <label className="text-xs font-bold text-gray-500 mb-1 block">Nombre de la impresora en Windows *</label>
                  <input className={inp} value={form.printerName} onChange={(e) => setForm({ ...form, printerName: e.target.value })} placeholder="EPSON TM-T20 Receipt" list="windows-printer-names" />
                  <datalist id="windows-printer-names">
                    {discoveredWindows.map((p) => <option key={p.name} value={p.name} />)}
                  </datalist>
                  <p className="text-xs text-gray-400 mt-1">Debe coincidir exactamente con el nombre que tiene en "Dispositivos e impresoras" de ese equipo.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-gray-500 mb-1 block">IP de la impresora *</label>
                    <input className={`${inp} font-mono text-xs`} value={form.printerIp} onChange={(e) => setForm({ ...form, printerIp: e.target.value })} placeholder="192.168.1.50" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 mb-1 block">Puerto</label>
                    <input type="number" className={inp} value={form.printerPort} onChange={(e) => setForm({ ...form, printerPort: Number(e.target.value) })} />
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs font-bold text-gray-500 mb-1 block">Ancho de papel (columnas)</label>
                <input type="number" className={inp} value={form.paperWidth} onChange={(e) => setForm({ ...form, paperWidth: Number(e.target.value) })} placeholder="48 = 80mm, 32 = 58mm" />
              </div>
              <p className="text-xs text-gray-400">
                Se creará en la sede <strong>{sedes.find((s) => s.id === sedeId)?.name}</strong>.
              </p>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={closeModal} className={`${btn} border border-gray-200 text-gray-600 hover:bg-gray-50`}>Cancelar</button>
              <button onClick={handleSave} disabled={saving} className={`${btn} bg-supernova-500 text-white hover:bg-supernova-600 disabled:opacity-60`}>
                <Save size={14} /> {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTicketPreview && <TicketPreviewModal onClose={() => setShowTicketPreview(false)} />}
    </div>
  );
}
